import { Suspense, useEffect } from "react";
import { HashRouter, Routes, Route, Navigate, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider } from "./Context/AuthContext";
import { ActiveSpaceProvider } from "./Map/context/ActiveSpaceContext";
import { CompanyProvider } from "./Context/CompanyProvider";
import { StaffSessionProvider } from "./Context/StaffSessionContext";
import { isAdminHost } from "./Utils/constants";
import { lazyRoute } from "./Utils/lazyRoute";

// Access components (public)
import Login from './Access/components/Login';
import Register from './Access/components/Register';
import ForgotPassword from './Access/components/ForgotPassword';
import ResetPassword from './Access/components/ResetPassword';
import VerifyEmail from './Access/components/VerifyEmail';
import AuthLayout from './Access/components/AuthLayout';
import AuthCallback from "./Access/components/AuthCallback";
import LogoutCallback from "./Access/components/LogoutCallback";
import { getHashSearchParams } from "./Access/utils/authQueryParams";

// Route guards
import PublicRoute from "./Components/PublicRoute";

// Protected components
import AppMap from "./Pages/MapPage";
import { flushPendingWebGLReport } from "./Map/utils/webglSupport";
import PageBootScreen from "./Components/PageBootScreen";
import AnalyticsPageView from "./analytics/AnalyticsPageView";
// Captura errores de render en toda la app y muestra pantalla amigable (ver ErrorBoundary/).
// Reporte al backend: POST /reportclienterror (src/services/clientErrorService.js).
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import { ToastProvider } from "./Components/Toast";
import { ConfirmDialogProvider } from "./Components/ConfirmDialog";
import AdminAuthLayout from "./Admin/components/AdminAuthLayout";
import AdminBootScreen from "./Admin/components/AdminBootScreen";
import StaffRoute from "./Admin/components/StaffRoute";
import { BrandsModalProvider } from "./Components/BrandsModal/BrandsModalContext";

const SharedListPage = lazyRoute(() => import("./Pages/SharedListPage"));
const SharedLotPage = lazyRoute(() => import("./Pages/SharedLotPage"));
const SharedUserLotPage = lazyRoute(() => import("./Pages/SharedUserLotPage"));
const ShowroomPage = lazyRoute(() => import("./Pages/ShowroomPage"));
// Informe de anuncios para el propietario: link público con token, sin sesión (Meta Ads, docs/maps/meta-ads.md).
const SharedUnitReportPage = lazyRoute(() => import("./Pages/SharedUnitReportPage"));
// Vuelta del login de Meta (Meta Ads, docs/maps/meta-ads.md).
const MetaCallbackPage = lazyRoute(() => import("./Pages/MetaCallbackPage"));

// Un solo chunk para toda la consola: navegar entre secciones no vuelve a pedir red.
const AdminShell = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminShell"));
const AdminStaffPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminStaffPage"));
const AdminCompaniesPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminCompaniesPage"));
const AdminCompanyMembersPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminCompanyMembersPage"));
const AdminDashboardPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminDashboardPage"));
const AdminErrorsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminErrorsPage"));
const AdminFeedbackPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminFeedbackPage"));
const AdminReferencePlacesPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Admin/components/AdminReferencePlacesPage"));

function getInviteToken(searchParams) {
  return searchParams.get('token') || getHashSearchParams().get('token') || '';
}

/** Deep links del mail de invite: #/shared/list?token=… → página de lista compartida */
function SharedListInviteRedirect() {
  const [params] = useSearchParams();
  const token = getInviteToken(params);
  if (!token) return <Navigate to="/app" replace />;
  return <Navigate to={`/shared/${encodeURIComponent(token)}`} replace />;
}

/** Deep links del mail de invite: #/shared/lot?token=… → página de lote compartido */
function SharedLotInviteRedirect() {
  const [params] = useSearchParams();
  const token = getInviteToken(params);
  if (!token) return <Navigate to="/app" replace />;
  return <Navigate to={`/shared-user-lot/${encodeURIComponent(token)}`} replace />;
}

/** Legacy `/#/showroom/...` → `/#/lista/:id` (el GET siempre va por UUID). */
function CadShowroomLegacyRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  if (!id) return <Navigate to="/app" replace />;
  return <Navigate to={`/lista/${encodeURIComponent(id)}${search || ''}`} replace />;
}

function MapLayout() {
  const location = useLocation();
  const path = location.pathname;

  const isMapRoute = path === "/app";

  // Sin WebGL, en login/registro el aviso no se reporta (el mapa es solo fondo);
  // si después pasa a /app u otra ruta, sale una vez.
  useEffect(() => {
    flushPendingWebGLReport({ pathname: path });
  }, [path]);

  const showControls = isMapRoute;
  const showStatusOverlays = isMapRoute;
  const showHeader = true;

  return (
    <>
      <AppMap
        showControls={showControls}
        showStatusOverlays={showStatusOverlays}
        showHeader={showHeader}
      />
      <Outlet />
    </>
  );
}

function AdminRoutes() {
  return (
    <Route
      path="/admin"
      element={(
        <StaffRoute>
          <Suspense fallback={<AdminBootScreen />}>
            <AdminShell />
          </Suspense>
        </StaffRoute>
      )}
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<AdminDashboardPage />} />
      <Route path="staff" element={<AdminStaffPage />} />
      <Route path="empresas" element={<AdminCompaniesPage />} />
      <Route path="empresas/:companyId" element={<AdminCompanyMembersPage />} />
      <Route path="lugares" element={<AdminReferencePlacesPage />} />
      <Route path="errores" element={<AdminErrorsPage />} />
      <Route path="feedback" element={<AdminFeedbackPage />} />
    </Route>
  );
}

function AuthFormRoutes({ layout: Layout }) {
  return (
    <Route element={<Layout />}>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
      <Route path="/verify" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
    </Route>
  );
}

function AppRoutes() {
  const adminHost = isAdminHost();

  return (
    <Routes>
      {AdminRoutes()}

      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/logout" element={<LogoutCallback />} />
      {/* Meta Ads: `META_OAUTH_REDIRECT_URI` = <origin>/meta-callback (index.html lo pasa al hash). */}
      <Route path="/meta-callback" element={<MetaCallbackPage />} />

      {adminHost ? (
        <>
          {AuthFormRoutes({ layout: AdminAuthLayout })}
          <Route path="/" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </>
      ) : (
        <>
          <Route element={<MapLayout />}>
            {AuthFormRoutes({ layout: AuthLayout })}
            <Route path="/app" element={null} />
            {/* Abre el panel Meta Ads y vuelve a /app (MapPage). */}
            <Route path="/meta-ads" element={null} />
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>

          <Route path="/shared/list" element={<SharedListInviteRedirect />} />
          <Route path="/shared/lot" element={<SharedLotInviteRedirect />} />
          <Route path="/shared/:token" element={<SharedListPage />} />
          <Route path="/shared-lot/:lotId" element={<SharedLotPage />} />
          <Route path="/shared-user-lot/:token" element={<SharedUserLotPage />} />
          {/* Informe de anuncios por link con vencimiento: público (sin login ni MapLayout). */}
          <Route path="/informe/:token" element={<SharedUnitReportPage />} />
          {/* CAD visitante: /#/lista/:id (no unificar con /shared — otro UUID/contrato) */}
          <Route path="/lista/:slug/:id" element={<ShowroomPage />} />
          <Route path="/lista/:id" element={<ShowroomPage />} />
          <Route path="/lista" element={<Navigate to="/app" replace />} />
          {/* Redirect temporal: mails/WhatsApp viejos con /showroom */}
          <Route path="/showroom/:slug/:id/manage" element={<CadShowroomLegacyRedirect />} />
          <Route path="/showroom/:slug/:id" element={<CadShowroomLegacyRedirect />} />
          <Route path="/showroom/:slug/manage" element={<Navigate to="/app" replace />} />
          <Route path="/showroom/:slug" element={<Navigate to="/app" replace />} />
          <Route path="/showroom" element={<Navigate to="/app" replace />} />
        </>
      )}
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ActiveSpaceProvider>
      <CompanyProvider>
        <ToastProvider>
        <ConfirmDialogProvider>
        <BrandsModalProvider>
        <HashRouter>
          <StaffSessionProvider>
          <ErrorBoundary>
          <AnalyticsPageView />
          <Suspense fallback={<PageBootScreen />}>
          <AppRoutes />
          </Suspense>
          </ErrorBoundary>
          </StaffSessionProvider>
        </HashRouter>
        </BrandsModalProvider>
        </ConfirmDialogProvider>
        </ToastProvider>
      </CompanyProvider>
      </ActiveSpaceProvider>
    </AuthProvider>
  );
}

export default App;
